import { z } from "zod";
import { reauthProof } from "@/shared/reauth";
import { personRole } from "@/shared/roles";

/** Shown as a word beside the role, never by colour alone. */
const personState = z.enum(["active", "invited", "deactivated"]);

/**
 * A person, or an invitation nobody has accepted yet. `PERSON_ROW_FIELDS` in `lib/people-view.ts`
 * is the allow-list that builds it.
 */
export const personRow = z.object({
  /** `per_…` for a person, `ivt_…` for an invitation. */
  id: z.string(),
  kind: z.enum(["person", "invitation"]),
  /** An invitation shows its address until somebody accepts it. */
  name: z.string(),
  email: z.string(),
  role: personRole,
  state: personState,
  /** Null for an invitation. */
  joinedAt: z.iso.datetime().nullable(),
  /**
   * The delta read and the optimistic wrapper compare against it to drop an older answer. On an
   * invitation it is also when it was last sent: sending again is the only write a pending one takes.
   */
  updatedAt: z.iso.datetime(),
  /**
   * The live projects they belong to that the reader may see, in the order they joined. Ids, not
   * names: a rename then changes no person row, and the screen names them from its project list.
   */
  projectIds: z.array(z.string()),
  photoKey: z.string().nullable(),
  /** Shown beside the name wherever a person is picked or found, ⌘K included. */
  jobTitle: z.string().nullable(),
  /**
   * Absent, not null, for a reader `seesLastSeen` in `roles.ts` refuses: their allow-list leaves the
   * field out. Null is "never seen"; missing is "not for you".
   */
  lastSeenAt: z.iso.datetime().nullable().optional(),
});

const personDetail = personRow.extend({
  /** Null for somebody who created the company or joined through the company-wide link. */
  invitedByName: z.string().nullable(),
  invitedAt: z.iso.datetime().nullable(),
  /** Tasks they hold, completed since the 1st in the company's zone, that the reader can see. */
  doneThisMonth: z.number().int(),
  removedAt: z.iso.datetime().nullable(),
  /** Null while active, after they left on their own, or when the remover's row cannot be read. */
  removedByName: z.string().nullable(),
  /** Set by an Admin; null reads as the day they joined. */
  startOn: z.iso.date().nullable(),
});

/** Anything unreadable falls back to no search rather than refusing the page. */
export const peopleSearch = z.object({
  q: z.string().max(100).catch("").default(""),
});

export const PEOPLE_SEARCH_DEFAULTS = { q: "" } as const;

const PERSON_VIEWS = ["tasks", "attendance", "leave", "pay"] as const;

/**
 * Which tab of a person's page is open; Tasks is the page with no `view` in the address. Leave's
 * dialogs and panel are open while the address says so, so Back closes them.
 */
export const personSearch = z.object({
  view: z.enum(PERSON_VIEWS).catch("tasks").default("tasks"),
  add: z.boolean().optional().catch(undefined),
  cancel: z.string().optional().catch(undefined),
  decide: z.string().optional().catch(undefined),
});

export const PERSON_SEARCH_DEFAULTS = { view: "tasks" } as const;

/** Asked for only when making, taking or restoring an Admin from a session older than a day. */
export const accessProofInput = z.object(reauthProof);

export const changeRoleInput = z.object({ role: personRole, ...reauthProof });

export const startDateInput = z.object({ startOn: z.iso.date() });

/** `details` on a 409 from a role change or a removal that would leave the company without an admin. */
export const lastAdminDetails = z.object({ reason: z.literal("last_admin") });

export type PersonRow = z.infer<typeof personRow>;
export type PersonDetail = z.infer<typeof personDetail>;
export type PersonState = z.infer<typeof personState>;
export type PersonView = (typeof PERSON_VIEWS)[number];
export type PersonSearch = z.infer<typeof personSearch>;
export type ChangeRoleInput = z.infer<typeof changeRoleInput>;
export type StartDateInput = z.infer<typeof startDateInput>;
export type LastAdminDetails = z.infer<typeof lastAdminDetails>;
